import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {getISODateString} from 'Quoting/utils/dateUtils';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getGPLUPolicySummaryPropertyPaths} from './GPLUPolicySummaryPropertyPaths';
import './PolicySummaryStyles.sass';
import {GPLUCoverageCode} from '../Plan/PlanTemplates/GWL/gpluConstants';
import {validateNumberOfLives} from 'Quoting/utils/validationHelpers';

export const GPLUPolicySummary = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedProduct} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = getGPLUPolicySummaryPropertyPaths();

  const numberOfLivesValidationResult = validateNumberOfLives(getQuoteFieldValue(propertyPaths.numberOfEligibleLives));

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'row-6d0cb0e4-96bb-4897-b4a6-4e58184b8673'},
      items: [
        {
          key: propertyPaths.packageCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Product Type"
              id={propertyPaths.packageCd}
              value={getQuoteFieldValue(propertyPaths.packageCd)}
              onChange={e => updateQuoteFieldValue(propertyPaths.packageCd, e.target.value)}
              dropdownVariant="AttributeValue"
              searchKey="GPLUMasterBenefitPolicyPackage.packageCd"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 12, md: 4},
        },
        {
          key: 'a650d36f-2f36-49bb-891a-b92c607df2d6',
          element: (
            <div className="gwl-row-1">
              <QuoteFormElement
                elementType="DatePicker"
                label="Quote Creation Date"
                id={propertyPaths.txCreateDate}
                value={getQuoteFieldValue(propertyPaths.txCreateDate)}
                onChange={date => updateQuoteFieldValue(propertyPaths.txCreateDate, getISODateString(date))}
                disabled
                required
              />
              <QuoteFormElement
                elementType="DatePicker"
                label="Policy Effective Date"
                id={propertyPaths.termEffectiveDate}
                value={getQuoteFieldValue(propertyPaths.termEffectiveDate)}
                onChange={date => updateQuoteFieldValue(propertyPaths.termEffectiveDate, getISODateString(date))}
                disabled
                required
              />
            </div>
          ),
          props: {xs: 12, sm: 12, md: 4},
        },

        {
          key: propertyPaths.rateGuaranteeMonths,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Rate Guarantee (Months)"
              id={propertyPaths.rateGuaranteeMonths}
              value={getQuoteFieldValue(propertyPaths.rateGuaranteeMonths)}
              onChange={e => updateQuoteFieldValue(propertyPaths.rateGuaranteeMonths, e.target.value)}
              type="number"
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 4},
        },
      ],
    },
    {
      rowProps: {key: 'row-19a3b407-4ed4-4188-904f-ee260b10bd44'},
      items: [
        {
          key: propertyPaths.methodOfDelivery,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Delivery Model"
              id={propertyPaths.methodOfDelivery}
              value={getQuoteFieldValue(propertyPaths.methodOfDelivery)}
              onChange={e => updateQuoteFieldValue(propertyPaths.methodOfDelivery, e.target.value)}
              dropdownVariant="Lookup"
              searchKey={`${selectedProduct?.code}DeliveryModel`}
              required
              disabled
            />
          ),
        },
        {
          key: propertyPaths.riskStateCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Situs State"
              id={propertyPaths.riskStateCd}
              value={getQuoteFieldValue(propertyPaths.riskStateCd)}
              onChange={e => updateQuoteFieldValue(propertyPaths.riskStateCd, e.target.value)}
              dropdownVariant="Lookup"
              searchKey={`${selectedProduct?.code}SitusState`}
              required
              disabled
            />
          ),
        },
        {
          key: propertyPaths.maturityAge,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maturity Age"
              id={propertyPaths.maturityAge}
              value={getQuoteFieldValue(propertyPaths.maturityAge)}
              onChange={e => updateQuoteFieldValue(propertyPaths.maturityAge, e.target.value)}
              type="number"
              disabled
              required
            />
          ),
        },
      ],
    },
    {
      rowProps: {key: 'row-1aedfde4-2580-442b-8827-9a65aa5de50f'},
      items: [
        {
          key: propertyPaths.carrierName,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Prior Carrier Name"
              id={propertyPaths.carrierName}
              value={getQuoteFieldValue(propertyPaths.carrierName)}
              onChange={e => updateQuoteFieldValue(propertyPaths.carrierName, e.target.value)}
            />
          ),
        },
        {
          key: propertyPaths.carrierPolicyNo,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Prior Carrier Policy Number"
              id={propertyPaths.carrierPolicyNo}
              value={getQuoteFieldValue(propertyPaths.carrierPolicyNo)}
              onChange={e => updateQuoteFieldValue(propertyPaths.carrierPolicyNo, e.target.value)}
            />
          ),
        },
      ],
    },
    {
      rowProps: {key: 'row-2e60d108-adcc-47bf-8a36-29e98fd92fdd'},
      items: [
        {
          key: propertyPaths.numberOfEligibleLives,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Number of Eligible Lives"
              id={propertyPaths.numberOfEligibleLives}
              value={getQuoteFieldValue(propertyPaths.numberOfEligibleLives)}
              onChange={e => updateQuoteFieldValue(propertyPaths.numberOfEligibleLives, e.target.value)}
              type="number"
              error={Boolean(numberOfLivesValidationResult?.error)}
              helperText={numberOfLivesValidationResult?.errorMessage}
              disabled
              required
            />
          ),
        },
        {
          key: propertyPaths.doliMaturAge,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maturity Age for DOLI Test"
              id={propertyPaths.doliMaturAge}
              value={getQuoteFieldValue(propertyPaths.doliMaturAge)}
              onChange={e => updateQuoteFieldValue(propertyPaths.doliMaturAge, e.target.value)}
              type="number"
              disabled
              required
            />
          ),
        },
      ],
    },
    {
      rowProps: {key: 'row-575b734e-919f-4066-9409-20594fb50406'},
      items: [
        {
          key: propertyPaths.sicCode,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="SIC Code"
              id={propertyPaths.sicCode}
              value={getQuoteFieldValue(propertyPaths.sicCode)}
              onChange={e => updateQuoteFieldValue(propertyPaths.sicCode, e.target.value)}
              disabled
              required
            />
          ),
        },
        {
          key: propertyPaths.sicDescription,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="SIC Description"
              id={propertyPaths.sicDescription}
              value={getQuoteFieldValue(propertyPaths.sicDescription)}
              onChange={e => updateQuoteFieldValue(propertyPaths.sicDescription, e.target.value)}
              disabled
              required
            />
          ),
        },
      ],
    },
    {
      rowProps: {key: 'row-b3227ab0-25da-41d9-ac66-1b8abb325d7f'},
      items: [
        {
          key: propertyPaths.quoteUWOffer,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Quote UW Offer"
              id={propertyPaths.quoteUWOffer}
              value={getQuoteFieldValue(propertyPaths.quoteUWOffer)}
              onChange={e => updateQuoteFieldValue(propertyPaths.quoteUWOffer, e.target.value)}
              dropdownVariant="AttributeValue"
              searchKey="GPLUMasterPolicyDetail.quoteUWOffer"
              required
              disabled
            />
          ),
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Policy Summary" collapsible expanded>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
